import './CompanyForm.css';
import CompanyAdminPicker from '../components/CompanyAdminPicker';
import React, { useState, useEffect } from 'react';
import { getAuthHeaders } from '../utils/authHeaders';
import CompanyModulePicker from '../components/CompanyModulePicker';
import { useParams, useNavigate } from 'react-router-dom';
const API = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000/api';

const COUNTRY_CODES = [
  { abbr: 'LB', code: '+961' }, { abbr: 'AE', code: '+971' }, { abbr: 'SA', code: '+966' },
  { abbr: 'QA', code: '+974' }, { abbr: 'KW', code: '+965' }, { abbr: 'BH', code: '+973' },
  { abbr: 'OM', code: '+968' }, { abbr: 'JO', code: '+962' }, { abbr: 'SY', code: '+963' },
  { abbr: 'IQ', code: '+964' }, { abbr: 'EG', code: '+20' },  { abbr: 'US', code: '+1' },
  { abbr: 'GB', code: '+44' },  { abbr: 'FR', code: '+33' },  { abbr: 'DE', code: '+49' },
  { abbr: 'IT', code: '+39' },  { abbr: 'ES', code: '+34' },  { abbr: 'TR', code: '+90' },
  { abbr: 'CY', code: '+357' }, { abbr: 'AU', code: '+61' },  { abbr: 'IN', code: '+91' },
  { abbr: 'CN', code: '+86' }
];

const COUNTRY_CITY_MAP = {
  'Lebanon': ['Beirut', 'Tripoli', 'Sidon', 'Tyre', 'Zahle', 'Jounieh', 'Baalbek', 'Nabatieh', 'Aley'],
  'UAE': ['Dubai', 'Abu Dhabi', 'Sharjah', 'Ajman', 'Ras Al Khaimah', 'Fujairah', 'Umm Al Quwain'],
  'Saudi Arabia': ['Riyadh', 'Jeddah', 'Mecca', 'Medina', 'Dammam', 'Khobar', 'Tabuk'],
  'Qatar': ['Doha', 'Al Wakrah', 'Al Khor', 'Al Rayyan', 'Lusail'],
  'Kuwait': ['Kuwait City', 'Al Ahmadi', 'Hawalli', 'Salmiya', 'Al Jahra'],
  'Bahrain': ['Manama', 'Muharraq', 'Riffa', 'Hamad Town', 'Isa Town'],
  'Oman': ['Muscat', 'Salalah', 'Sohar', 'Nizwa', 'Sur'],
  'Jordan': ['Amman', 'Zarqa', 'Irbid', 'Aqaba', 'Jerash'],
  'Syria': ['Damascus', 'Aleppo', 'Homs', 'Latakia', 'Hama'],
  'Iraq': ['Baghdad', 'Basra', 'Erbil', 'Mosul', 'Najaf'],
  'Egypt': ['Cairo', 'Alexandria', 'Giza', 'Sharm El Sheikh', 'Luxor'],
  'USA': ['New York', 'Los Angeles', 'Chicago', 'Houston', 'Miami'],
  'UK': ['London', 'Birmingham', 'Manchester', 'Glasgow', 'Liverpool'],
  'France': ['Paris', 'Marseille', 'Lyon', 'Toulouse', 'Nice'],
  'Germany': ['Berlin', 'Munich', 'Frankfurt', 'Hamburg', 'Cologne'],
  'Italy': ['Rome', 'Milan', 'Naples', 'Turin', 'Venice'],
  'Spain': ['Madrid', 'Barcelona', 'Valencia', 'Seville', 'Zaragoza'],
  'Turkey': ['Istanbul', 'Ankara', 'Izmir', 'Antalya', 'Bursa'],
  'Cyprus': ['Nicosia', 'Limassol', 'Larnaca', 'Paphos'],
  'Australia': ['Sydney', 'Melbourne', 'Brisbane', 'Perth', 'Adelaide'],
  'India': ['Mumbai', 'Delhi', 'Bangalore', 'Hyderabad', 'Chennai'],
  'China': ['Beijing', 'Shanghai', 'Shenzhen', 'Guangzhou', 'Chengdu']
};

const COUNTRIES_LIST = Object.keys(COUNTRY_CITY_MAP).sort();

function CompanyEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);

  const [availableAdmins, setAvailableAdmins] = useState([]);
  const [availableModules, setAvailableModules] = useState([]);

  const [formData, setFormData] = useState({
    name: '',
    legal_name: '',
    countryCode: '+961',
    phone: '',
    country: '',
    city: '',
    admin_ids: [],
    module_ids: []
  });

  useEffect(() => {
    let active = true;
    const api = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000/api';
    const get = async path => {
      const response = await fetch(`${api}${path}`, { headers: getAuthHeaders() });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Unable to load company setup.');
      return data;
    };
    setInitialLoading(true);
    Promise.all([get('/users'), get('/modules'), get(`/companies/${id}`)])
      .then(([users, modules, company]) => {
        if (!active) return;
        setAvailableAdmins(Array.isArray(users) ? users : (Array.isArray(users.users) ? users.users : (Array.isArray(users.data) ? users.data : [])));
        setAvailableModules(Array.isArray(modules) ? modules : (Array.isArray(modules.data) ? modules.data : []));
        const phoneParts = (company.phone || '').split(' ');
        const locationParts = (company.location || '').split(',').map(part => part.trim());
        setFormData({ code: company.code || '', name: company.name || '', legal_name: company.legal_name || '',
          countryCode: phoneParts[0] || '+961', phone: phoneParts.slice(1).join(' '),
          city: locationParts[0] || '', country: locationParts[1] || '',
          admin_ids: (company.administrators || []).map(user => Number(user.id)),
          module_ids: (company.assigned_modules || []).map(module => Number(module.id)) });
      }).catch(error => { if (active) setError(error.message); })
      .finally(() => { if (active) setInitialLoading(false); });
    return () => { active = false; };
  }, [id]);

  const capitalizeWords = (str) => str.replace(/\b\w/g, char => char.toUpperCase());

  const handleChange = (e) => {
    const { name, value } = e.target;
    let finalValue = value;
    if (name === 'name') finalValue = capitalizeWords(value);

    if (name === 'country') {
      setFormData({ ...formData, country: finalValue, city: '' });
    } else {
      setFormData({ ...formData, [name]: finalValue });
    }
  };

  const handleCancel = () => {
    navigate('/companies');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const payload = {
      name: formData.name,
      code: (formData.code || '').trim() || null,
      legal_name: formData.legal_name,
      phone: `${formData.countryCode} ${formData.phone}`,
      location: `${formData.city}, ${formData.country}`,
      admin_ids: formData.admin_ids,
      modules: formData.module_ids
    };

    fetch(`${API}/companies/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
      },
      body: JSON.stringify(payload)
    })
      .then((res) => {
        if (!res.ok) throw new Error('Failed to update company. Please check your backend connection.');
        return res.json();
      })
      .then(() => {
        setLoading(false);
        navigate('/companies');
      })
      .catch((err) => {
        setLoading(false);
        setError(err.message);
      });
  };

  const availableCities = formData.country ? [...(COUNTRY_CITY_MAP[formData.country] || [])].sort() : [];

  if (initialLoading) {
    return <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>Loading company details...</div>;
  }

  return (
    <div className="company-edit-page">
      <header className="company-edit-header"><h2>Edit Company</h2><div><button type="button" onClick={handleCancel}>Cancel</button><button className="company-save" type="submit" form="company-edit-form" disabled={loading}>{loading ? 'Saving...' : 'Save Changes'}</button></div></header>
      {error && <div role="alert" className="company-edit-error">{error}</div>}
      <form id="company-edit-form" onSubmit={handleSubmit} className="company-edit-form">
        <section className="company-edit-details">
          <h3>Company details</h3>
          <div className="company-edit-fields">
            <label>Company Name<input name="name" value={formData.name} onChange={handleChange} required /></label>
            <label>Company ID / Code<input aria-label="Company ID / Code" value={formData.code || ''} onChange={e=>setFormData(p=>({...p,code:e.target.value.toUpperCase()}))} placeholder="e.g. ACA" maxLength={40} /><small>Your company prefix, such as ACA.</small></label>
            <label>Legal Company Name<input name="legal_name" value={formData.legal_name} onChange={handleChange} /></label>
            <label>Phone Number<div className="company-phone"><select name="countryCode" value={formData.countryCode} onChange={handleChange}>{COUNTRY_CODES.map(c=><option key={c.abbr} value={c.code}>{c.abbr} {c.code}</option>)}</select><input name="phone" value={formData.phone} onChange={handleChange} required /></div></label>
            <label>Country<select name="country" value={formData.country} onChange={handleChange} required><option value="">Select country</option>{COUNTRIES_LIST.map(c=><option key={c}>{c}</option>)}</select></label>
            <label>City / Location<input name="city" value={formData.city || ''} onChange={handleChange} list="company-cities" /><datalist id="company-cities">{availableCities.map(c=><option key={c} value={c} />)}</datalist></label>
          </div>
          <p className="company-privacy-note">Company administrators manage privacy in Roles &amp; Permissions → Settings.</p>
        </section>
        <section className="company-edit-admins"><CompanyAdminPicker users={availableAdmins} selected={formData.admin_ids} onChange={ids=>setFormData(p=>({...p,admin_ids:ids}))} /></section>
        <section className="company-edit-modules"><CompanyModulePicker modules={availableModules} selected={formData.module_ids} onChange={selected=>setFormData(p=>({...p,module_ids:selected}))} /></section>
      </form>
    </div>
  );
}

export default CompanyEdit;
